import { useState, useEffect, useCallback } from 'react';

import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import DarkModeIcon from '@/material-icons/400-24px/toggle_on.svg?react';
import LightModeIcon from '@/material-icons/400-24px/toggle_off.svg?react';
import LanguageIcon from '@/material-icons/400-24px/language.svg?react';

const messages = defineMessages({
  theme: { id: 'theme_selector.theme', defaultMessage: 'Theme' },
  language: { id: 'theme_selector.language', defaultMessage: 'Language' },
  dark: { id: 'theme_selector.dark', defaultMessage: 'Dark' },
  light: { id: 'theme_selector.light', defaultMessage: 'Light' },
});

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ar', name: 'العربية' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'zh', name: '中文' },
  { code: 'hi', name: 'हिन्दी' },
];

export const ThemeLanguageSelector: React.FC = () => {
  const intl = useIntl();
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored) return stored as 'dark' | 'light';
      const htmlScheme = document.documentElement.getAttribute('data-color-scheme');
      return htmlScheme === 'light' ? 'light' : 'dark';
    }
    return 'dark';
  });
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('locale') || 'en';
    }
    return 'en';
  });
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const htmlElement = document.documentElement;
    if (theme === 'light') {
      htmlElement.setAttribute('data-color-scheme', 'light');
    } else {
      htmlElement.removeAttribute('data-color-scheme');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleThemeToggle = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const handleLanguageChange = useCallback((langCode: string) => {
    setLanguage(langCode);
    localStorage.setItem('locale', langCode);
    // Reload to apply new language
    window.location.reload();
  }, []);

  const currentLanguage = SUPPORTED_LANGUAGES.find((lang) => lang.code === language) || SUPPORTED_LANGUAGES[0];

  return (
    <div className='theme-language-selector'>
      <button
        className='theme-language-selector__button'
        onClick={() => setShowMenu(!showMenu)}
        aria-label={intl.formatMessage(messages.theme)}
        title={intl.formatMessage(messages.theme)}
      >
        {theme === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
        <span className='theme-language-selector__current-lang'>{currentLanguage.name}</span>
      </button>

      {showMenu && (
        <div className='theme-language-selector__dropdown'>
          <div className='theme-language-selector__section'>
            <h4 className='theme-language-selector__section-title'>
              <FormattedMessage id='theme_selector.theme' defaultMessage='Theme' />
            </h4>
            <div className='theme-language-selector__options'>
              <button
                className={classNames('theme-language-selector__option', { active: theme === 'dark' })}
                onClick={() => handleThemeToggle()}
              >
                <DarkModeIcon />
                <span>{intl.formatMessage(messages.dark)}</span>
              </button>
              <button
                className={classNames('theme-language-selector__option', { active: theme === 'light' })}
                onClick={() => handleThemeToggle()}
              >
                <LightModeIcon />
                <span>{intl.formatMessage(messages.light)}</span>
              </button>
            </div>
          </div>

          <div className='theme-language-selector__section'>
            <h4 className='theme-language-selector__section-title'>
              <FormattedMessage id='theme_selector.language' defaultMessage='Language' />
            </h4>
            <div className='theme-language-selector__options theme-language-selector__options--scroll'>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  className={classNames('theme-language-selector__option', {
                    active: language === lang.code,
                  })}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <LanguageIcon />
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
